import { TRPCClientError } from "@trpc/client";
import { AxiosError } from "axios";
import config from "../../config/environment";

/**
 * Standardized error handling utilities for the application
 *
 * Features:
 * - Consistent error message extraction from different error types
 * - Categorization of errors by type
 * - Field error mapping for form validation
 * - Support for both tRPC and Axios errors
 */

// Define error categories
export enum ErrorCategory {
  NETWORK = "network",
  VALIDATION = "validation",
  AUTHENTICATION = "authentication",
  AUTHORIZATION = "authorization",
  NOT_FOUND = "not_found",
  SERVER = "server",
  UNKNOWN = "unknown",
}

// Interface for standardized error response
export interface StandardError {
  message: string;
  category: ErrorCategory;
  statusCode?: number;
  fields?: Record<string, string>;
  originalError?: unknown;
}

/**
 * Extract error message and details from a variety of error types
 *
 * @param error - Any error object that might be thrown
 * @returns StandardError with consistent format
 */
export const extractErrorDetails = (error: unknown): StandardError => {
  // Default error response
  const standardError: StandardError = {
    message: "An unexpected error occurred",
    category: ErrorCategory.UNKNOWN,
  };

  try {
    // Handle TRPC client errors
    if (error instanceof TRPCClientError) {
      const trpcError = error as TRPCClientError<any>;
      standardError.statusCode = trpcError.data?.httpStatus;

      // Use the most specific error message available
      if (trpcError.data?.message) {
        standardError.message = trpcError.data.message;
      } else if (trpcError.message) {
        standardError.message = trpcError.message;
      }

      // Extract field validation errors if present
      if (trpcError.data?.fieldErrors) {
        standardError.fields = trpcError.data.fieldErrors;
        standardError.category = ErrorCategory.VALIDATION;
      }

      // Categorize by status code
      if (standardError.statusCode) {
        if (standardError.statusCode === 401) {
          standardError.category = ErrorCategory.AUTHENTICATION;
        } else if (standardError.statusCode === 403) {
          standardError.category = ErrorCategory.AUTHORIZATION;
        } else if (standardError.statusCode === 404) {
          standardError.category = ErrorCategory.NOT_FOUND;
        } else if (
          standardError.statusCode >= 400 &&
          standardError.statusCode < 500
        ) {
          standardError.category = ErrorCategory.VALIDATION;
        } else if (standardError.statusCode >= 500) {
          standardError.category = ErrorCategory.SERVER;
        }
      }
    }
    // Handle Axios errors
    else if (error instanceof AxiosError) {
      standardError.statusCode = error.response?.status;

      // Extract message from response data if possible
      if (error.response?.data?.message) {
        standardError.message = error.response.data.message;
      } else if (error.message) {
        standardError.message = error.message;
      }

      // Field errors may be in response data
      if (error.response?.data?.errors || error.response?.data?.fieldErrors) {
        standardError.fields =
          error.response.data.errors || error.response.data.fieldErrors;
        standardError.category = ErrorCategory.VALIDATION;
      }

      // Categorize network errors
      if (error.code === "ECONNABORTED" || !error.response) {
        standardError.category = ErrorCategory.NETWORK;
        standardError.message = "Network error. Please check your connection.";
      }
      // Categorize by status code
      else if (standardError.statusCode) {
        if (standardError.statusCode === 401) {
          standardError.category = ErrorCategory.AUTHENTICATION;
        } else if (standardError.statusCode === 403) {
          standardError.category = ErrorCategory.AUTHORIZATION;
        } else if (standardError.statusCode === 404) {
          standardError.category = ErrorCategory.NOT_FOUND;
        } else if (
          standardError.statusCode >= 400 &&
          standardError.statusCode < 500
        ) {
          standardError.category = ErrorCategory.VALIDATION;
        } else if (standardError.statusCode >= 500) {
          standardError.category = ErrorCategory.SERVER;
        }
      }
    }
    // Handle standard Error objects
    else if (error instanceof Error) {
      standardError.message = error.message;

      // Network error detection for fetch-based errors
      if (
        error.message.includes("Network") ||
        error.message.includes("fetch") ||
        error.message.includes("Failed to fetch")
      ) {
        standardError.category = ErrorCategory.NETWORK;
      }
    }
    // Handle string errors
    else if (typeof error === "string") {
      standardError.message = error;
    }

    // Add the original error for debugging
    standardError.originalError = error;
  } catch (e) {
    // If error processing fails, return the default error
    console.error("Error while processing error:", e);
  }

  return standardError;
};

/**
 * Formats an error message for user display, with special handling for specific error categories
 *
 * @param error - The error to format
 * @returns Formatted error message suitable for displaying to users
 */
export const formatErrorMessage = (error: unknown): string => {
  const standardError = extractErrorDetails(error);

  // Customize messages by category
  switch (standardError.category) {
    case ErrorCategory.NETWORK:
      return "Unable to connect to the server. Please check your internet connection and try again.";

    case ErrorCategory.AUTHENTICATION:
      return "Authentication failed. Please log in again.";

    case ErrorCategory.AUTHORIZATION:
      return "You do not have permission to perform this action.";

    case ErrorCategory.NOT_FOUND:
      return "The requested resource was not found.";

    case ErrorCategory.SERVER:
      return "The server encountered an error. Please try again later.";

    case ErrorCategory.VALIDATION:
      // For validation errors, we might want to show the specific message
      return standardError.message;

    case ErrorCategory.UNKNOWN:
    default:
      return standardError.message;
  }
};

/**
 * Logs an error with appropriate level and context
 *
 * @param error - The error to log
 * @param context - Additional context information
 */
export const logError = (error: unknown, context?: string): void => {
  const standardError = extractErrorDetails(error);

  // Only log detailed errors in development
  if (config.environment === "development") {
    console.group(`Error [${context || "Application"}]`);
    console.error("Error Details:", standardError);
    console.error("Original Error:", standardError.originalError);
    console.groupEnd();
  } else {
    // In production, log minimal information
    console.error(
      `Error [${context || "Application"}]:`,
      standardError.message,
      standardError.category,
      standardError.statusCode,
    );
  }
};
